// frontend/src/pages/ArtHouse/ArtworkPage.jsx
// Shop/Support links: show "Etsy • Hoodie" (platform + product type)
// Buy button appears in the corner on hover
// Sits directly under MoreByArtistRail
// Shorter + simpler implementation
//
// UPDATED: Favorites now uses DB table `public.artwork_favorites` via API:
// - GET  /artwork-favorites/status/:artworkId  -> { favorited, favoriteCount }
// - POST /artwork-favorites/toggle             -> { favorited, favoriteCount }

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Heart, MessageCircle, Search, Share2, Bookmark, Sparkles } from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { useArtists } from "../../context/ArtistContext";
import { fetchArtworkDetail } from "../../services/arthouseApi";
import { postArtworkComment } from "../../services/artworkCommentsApi";
import ThreadComposer from "../../features/comments/components/ThreadComposer.js";
import CommentThreadList from "../../features/comments/components/CommentThreadList.js";
import {
  mergeClubCommentFromServer,
  mergeViewerOntoOwnClubCommentIfNeeded,
  normalizeClubCommentRow,
  sliceCommentBranchByAnchorId,
} from "../../features/comments/utils/clubFeedPostUtils.js";
import {
  buildOptimisticLikeState,
  reconcileLikeStateFromResponse,
} from "../../utils/likeInteraction";

import MoreByArtistRail from "../../components/ArtHouse/MoreByArtistRail";

/* -----------------------
  small helpers
------------------------ */
const txt = (v, fb = "") => (v ?? "").toString().trim() || fb;

function safeUrl(u) {
  const s = String(u || "").trim();
  return s && (s.startsWith("http://") || s.startsWith("https://")) ? s : "";
}

function formatCount(n) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return "0";
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${num}`;
}

function safeDateLabel(v) {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  return String(v);
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

function getArtworkImage(a) {
  return (
    a?.full_image_url ||
    a?.fullImageUrl ||
    a?.image_full ||
    a?.imageFull ||
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    a?.url ||
    a?.src ||
    ""
  );
}

function asTags(artwork, topLevelTags) {
  const t =
    topLevelTags ??
    artwork?.tags ??
    artwork?.tags_text ??
    artwork?.tagsText ??
    artwork?.tag_text ??
    artwork?.tagText ??
    artwork?.tag_list ??
    artwork?.tagList ??
    artwork?.art_tags ??
    artwork?.artTags ??
    artwork?.labels ??
    artwork?.keywords ??
    null;

  if (!t) return [];
  if (Array.isArray(t)) {
    return t
      .map((v) => {
        if (!v) return null;
        if (typeof v === "string") return v.trim();
        if (typeof v === "object") return (v.label ?? v.key ?? v.name ?? v.slug ?? "").toString().trim();
        return null;
      })
      .filter(Boolean);
  }
  if (typeof t === "string") return t.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
}

function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

/* -----------------------
  merch: platform + type + hover buy
------------------------ */
function platformFromUrl(u) {
  try {
    const host = new URL(u).hostname.replace(/^www\./, "");
    const base = host.split(".")[0] || "Store";
    return base.length ? base[0].toUpperCase() + base.slice(1) : "Store";
  } catch {
    return "Store";
  }
}

function normalizeMerch(list = []) {
  const arr = Array.isArray(list) ? list : [];
  const items = arr
    .map((m) => {
      const url = safeUrl(m?.purchase_url ?? m?.purchaseUrl ?? m?.url ?? m?.href);
      if (!url) return null;

      const platform = txt(m?.platform, platformFromUrl(url));
      const type = txt(m?.product_type ?? m?.productType ?? m?.type_name ?? m?.type, "");
      const label = txt(m?.label ?? m?.button_label ?? m?.buttonLabel ?? m?.title, "");

      const productType = type || (label && label.length <= 26 ? label : "") || "Merch";
      const display = `${platform} • ${productType}`;

      const isPrimary = !!(m?.is_primary ?? m?.isPrimary);
      return { url, platform, productType, display, isPrimary };
    })
    .filter(Boolean);

  items.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  return items;
}

function ShopLinks({ merchLinks, max = 5 }) {
  const items = useMemo(() => normalizeMerch(merchLinks).slice(0, max), [merchLinks, max]);
  if (!items.length) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.35em] uppercase text-white/40">Shop</div>
        <div className="text-[11px] text-white/35">{items.length}</div>
      </div>

      <div className="space-y-2">
        {items.map((m) => (
          <a
            key={m.url}
            href={m.url}
            target="_blank"
            rel="noreferrer"
            className="
              group relative block
              rounded-2xl border border-white/10
              bg-white/[0.03] hover:bg-white/[0.06]
              shadow-[0_18px_70px_rgba(0,0,0,0.45)]
              px-4 py-3
              transition
            "
            title={`${m.platform} • ${m.productType}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white/85">{m.display}</div>
                <div className="truncate text-[12px] text-white/45">Opens in new tab</div>
              </div>

              <span
                className="
                  pointer-events-none absolute right-3 top-3
                  inline-flex items-center justify-center
                  h-8 px-3 rounded-full
                  border border-emerald-300/25
                  bg-emerald-400/10
                  text-[12px] font-semibold text-emerald-200
                  opacity-0 translate-y-[-2px]
                  group-hover:opacity-100 group-hover:translate-y-0
                  transition
                "
              >
                Buy →
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

/* -----------------------
  page
------------------------ */
export default function ArtHouseArtworkPage() {
  const { artworkId: paramId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { user } = useAuth();
  const { upsertArtist, getArtist } = useArtists();

  const seeded = location?.state?.artwork ?? null;
  const seededId = getArtworkId(seeded) ?? paramId;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const [toast, setToast] = useState("");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentLikeBusyById, setCommentLikeBusyById] = useState({});
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [commentSort, setCommentSort] = useState("new");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const [searchMode, setSearchMode] = useState(false);
  const [commentSearchText, setCommentSearchText] = useState("");
  const searchCommentsTriggerRef = useRef(null);
  const [activeReplyTargetId, setActiveReplyTargetId] = useState(null);
  const [inlineReplyText, setInlineReplyText] = useState("");
  const [inlineSending, setInlineSending] = useState(false);
  const [commentThreadActionErr, setCommentThreadActionErr] = useState("");
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState(null);
  /** Clubs-style “continue thread”: show anchor + descendants only (reframes deep chains without a separate route). */
  const [threadContinuationAnchorId, setThreadContinuationAnchorId] = useState(null);

  // DB-truth comment count
  const [commentsCount, setCommentsCount] = useState(0);

  // Favorites (saved artwork)
  const [isFavorited, setIsFavorited] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      setErr("");
      setLoading(true);

      try {
        let nextData = null;

        if (seededId) {
          nextData = await fetchArtworkDetail(seededId);
          if (nextData?.artwork?.artist) upsertArtist(nextData.artwork.artist);
        } else if (seeded) {
          nextData = { artwork: seeded };
        } else {
          setErr("Artwork not found.");
        }

        if (!alive) return;
        setData(nextData);
      } catch {
        if (!alive) return;
        setData(null);
        setErr("Failed to load artwork.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [seededId, seeded, upsertArtist]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1400);
    return () => clearTimeout(t);
  }, [toast]);

  const artwork = data?.artwork || seeded || null;

  // Canonical artwork id used everywhere (comments, likes, favorites, etc.)
  const id = getArtworkId(artwork) ?? seededId ?? paramId ?? null;

  const img = useMemo(() => getArtworkImage(artwork), [artwork]);
  const title = txt(artwork?.title, "Untitled");
  const description = txt(artwork?.description || artwork?.caption || artwork?.subtitle, "");

  const tags = useMemo(() => asTags(artwork, data?.tags), [artwork, data?.tags]);

  const stats = useMemo(() => {
    const a = artwork || {};
    return {
      views: a.views ?? a.view_count ?? 0,
      likes: a.likes ?? a.like_count ?? 0,
      favorites: a.favorites ?? a.favourite_count ?? a.favorite_count ?? 0,
      comments: a.comments ?? a.comment_count ?? 0,
      createdAt: a.createdAt ?? a.created_at ?? a.publishedAt ?? a.published_at ?? a.date ?? null,
    };
  }, [artwork]);

  const commentDeepLink = useMemo(() => {
    const raw = searchParams.get("comment");
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [searchParams]);

  const viewerId = user?.id ?? null;

  useEffect(() => {
    if (!sortMenuOpen) return;
    const onDoc = (e) => {
      if (sortMenuRef.current?.contains(e.target)) return;
      setSortMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sortMenuOpen]);

  useEffect(() => {
    if (!id) return;
    setActiveReplyTargetId(null);
    setInlineReplyText("");
    setSearchMode(false);
    setCommentSearchText("");
    setSortMenuOpen(false);
    setThreadContinuationAnchorId(null);
  }, [id]);

  useEffect(() => {
    if (threadContinuationAnchorId == null) return;
    const exists = comments.some(
      (c) => Number(c?.id ?? c?.comment_id) === threadContinuationAnchorId
    );
    if (!exists) setThreadContinuationAnchorId(null);
  }, [comments, threadContinuationAnchorId]);

  const commentsForThreadList = useMemo(() => {
    if (threadContinuationAnchorId == null) return comments;
    return sliceCommentBranchByAnchorId(comments, threadContinuationAnchorId);
  }, [comments, threadContinuationAnchorId]);

  const onArtworkContinueThread = useCallback(
    (parentCommentId, opts) => {
      const anchor = Number(parentCommentId);
      if (!Number.isFinite(anchor)) return;
      const exists = comments.some((c) => Number(c?.id ?? c?.comment_id) === anchor);
      if (!exists) return;
      setThreadContinuationAnchorId(anchor);
      const rawFocus = opts?.focusCommentId;
      const focusNum = Number(rawFocus);
      if (Number.isFinite(focusNum)) setPendingFocusCommentId(focusNum);
    },
    [comments]
  );

  useEffect(() => {
    if (!commentDeepLink || loadingComments || comments.length === 0) return;
    const t = window.setTimeout(() => {
      document
        .querySelector(`[data-comment-id="${commentDeepLink}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 120);
    return () => clearTimeout(t);
  }, [commentDeepLink, loadingComments, comments.length]);

  useEffect(() => {
    if (!pendingFocusCommentId || loadingComments) return;
    const t = window.setTimeout(() => {
      document
        .querySelector(`[data-comment-id="${pendingFocusCommentId}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
      setPendingFocusCommentId(null);
    }, 100);
    return () => clearTimeout(t);
  }, [pendingFocusCommentId, loadingComments, commentsForThreadList.length]);

  // Fetch comments (DB) + keep count synced
  useEffect(() => {
    if (!id) return;

    let alive = true;

    (async () => {
      setLoadingComments(true);
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const response = await fetch(`${API_BASE}/comments/artwork/${id}`, {
          credentials: "include",
        });

        if (!alive) return;

        if (response.ok) {
          setCommentThreadActionErr("");
          const json = await response.json();

          const list = Array.isArray(json?.comments) ? json.comments : [];
          const normalized = list
            .map((row) =>
              normalizeClubCommentRow({
                ...row,
                like_count: Number(row?.like_count) || 0,
                liked_by_viewer: Boolean(row?.liked_by_viewer),
              })
            )
            .filter(Boolean);
          setComments(normalized);

          // Prefer explicit count if backend returns it; otherwise list length
          const count = Number.isFinite(Number(json?.count)) ? Number(json.count) : list.length;
          setCommentsCount(count);

          // Keep local artwork object consistent (optional but helps UI)
          setData((prev) => {
            const cur = prev?.artwork;
            if (!cur) return prev;
            return {
              ...prev,
              artwork: {
                ...cur,
                comments: count,
                comment_count: count,
              },
            };
          });
        } else {
          const bodyText = await response.text().catch(() => "");
          let bodySnippet = bodyText;
          try {
            const parsed = JSON.parse(bodyText);
            bodySnippet = JSON.stringify(parsed);
          } catch {
            /* keep raw */
          }
          console.error("[ArtHouse] comments fetch failed", {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            body: bodySnippet?.slice?.(0, 2000) ?? bodySnippet,
          });
          setComments([]);
          setCommentsCount(0);
          setCommentThreadActionErr("Could not load comments. Try again later.");
        }
      } catch (err) {
        console.error("Error fetching comments:", err);
        if (alive) {
          setComments([]);
          setCommentsCount(0);
          setCommentThreadActionErr("Could not load comments. Try again later.");
        }
      } finally {
        if (alive) setLoadingComments(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  // Fetch favorite status + count from DB
  useEffect(() => {
    if (!id) return;

    let alive = true;

    (async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const res = await fetch(`${API_BASE}/artwork-favorites/status/${id}`, {
          credentials: "include",
        });

        if (!alive) return;

        if (res.ok) {
          const json = await res.json(); // { favorited, favoriteCount }
          const favorited = Boolean(json?.favorited);
          const favoriteCount = Number(json?.favoriteCount ?? 0);

          setIsFavorited(favorited);

          // keep UI count synced to DB
          setData((prev) => {
            const cur = prev?.artwork;
            if (!cur) return prev;
            return {
              ...prev,
              artwork: {
                ...cur,
                favorites: Number.isFinite(favoriteCount) ? favoriteCount : cur.favorites,
              },
            };
          });
        }
      } catch (e) {
        console.error("Error loading favorite status:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const rawArtist = artwork?.artist || artwork?.creator || null;
  const liveArtist = getArtist(rawArtist?.id) || rawArtist || null;

  const artistId = liveArtist?.id ?? liveArtist?.artist_id ?? liveArtist?.artistId ?? null;

  const artistName = txt(liveArtist?.display_name || liveArtist?.displayName || liveArtist?.name, "Artist");

  const isAI =
    String(artwork?.is_ai ?? artwork?.isAI ?? artwork?.ai ?? false) === "true" ||
    Boolean(artwork?.is_ai ?? artwork?.isAI ?? artwork?.ai) === true;

  const merchLinks = data?.merchLinks || artwork?.merchLinks || [];

  // actions
  const onLike = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    if (!id) return;
    const optimisticState = buildOptimisticLikeState({
      liked_by_viewer: artwork?.liked_by_viewer ?? artwork?.liked,
      like_count: artwork?.like_count ?? artwork?.likes,
    });

    setData((prev) => {
      const cur = prev?.artwork;
      if (!cur) return prev;
      return {
        ...prev,
        artwork: {
          ...cur,
          liked_by_viewer: optimisticState.optimisticLiked,
          liked: optimisticState.optimisticLiked,
          like_count: optimisticState.optimisticCount,
          likes: optimisticState.optimisticCount,
        },
      };
    });

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";
      const response = await fetch(`${API_BASE}/likes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_type: "artwork", target_id: Number(id) }),
      });

      if (!response.ok) throw new Error("Failed to toggle like.");
      const json = await response.json();
      const canonical = reconcileLikeStateFromResponse(json, optimisticState);
      setData((prev) => {
        const cur = prev?.artwork;
        if (!cur) return prev;
        return {
          ...prev,
          artwork: {
            ...cur,
            liked_by_viewer: canonical.liked_by_viewer,
            liked: canonical.liked_by_viewer,
            like_count: canonical.like_count,
            likes: canonical.like_count,
          },
        };
      });
      setToast(canonical.liked_by_viewer ? "Liked" : "Unliked");
    } catch (err) {
      console.error("Error toggling like:", err);
      setData((prev) => {
        const cur = prev?.artwork;
        if (!cur) return prev;
        return {
          ...prev,
          artwork: {
            ...cur,
            liked_by_viewer: optimisticState.prevLiked,
            liked: optimisticState.prevLiked,
            like_count: optimisticState.prevCount,
            likes: optimisticState.prevCount,
          },
        };
      });
      setToast("Failed to update like");
    }
  };

  // DB-backed toggle favorite (public.artwork_favorites)
  const onFavorite = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    if (!id) return;
    if (loadingFavorite) return;

    try {
      setLoadingFavorite(true);
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";

      const res = await fetch(`${API_BASE}/artwork-favorites/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ artwork_id: Number(id) }),
      });

      if (!res.ok) {
        setToast("Failed to update favourite");
        return;
      }

      const json = await res.json(); // { favorited, favoriteCount }
      const favorited = Boolean(json?.favorited);
      const favoriteCount = Number(json?.favoriteCount ?? 0);

      setIsFavorited(favorited);

      setData((prev) => {
        const cur = prev?.artwork;
        if (!cur) return prev;
        return {
          ...prev,
          artwork: {
            ...cur,
            favorites: Number.isFinite(favoriteCount) ? favoriteCount : cur.favorites,
          },
        };
      });

      setToast(favorited ? "Saved" : "Removed");
    } catch (e) {
      console.error("Error toggling favourite:", e);
      setToast("Failed to update favourite");
    } finally {
      setLoadingFavorite(false);
    }
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("Link copied");
    } catch {
      alert(url);
    }
  };

  const bumpArtworkCommentCount = useCallback((delta) => {
    setCommentsCount((prev) => Math.max(0, (Number(prev) || 0) + delta));
    setData((prev) => {
      const cur = prev?.artwork;
      if (!cur) return prev;
      const base = Number(cur.comments ?? cur.comment_count ?? 0) || 0;
      const next = Math.max(0, base + delta);
      return { ...prev, artwork: { ...cur, comments: next, comment_count: next } };
    });
  }, []);

  const submitReply = useCallback(
    async (payload = null) => {
      const text = String(payload?.text ?? replyText ?? "").trim();
      if (!text || !viewerId || !id || sending) return;
      try {
        setSending(true);
        setCommentThreadActionErr("");
        const created = await postArtworkComment({ artworkId: id, text });
        const normalized = mergeViewerOntoOwnClubCommentIfNeeded(
          normalizeClubCommentRow(created),
          viewerId,
          user ? { username: user.username, profile_picture: user.profile_picture } : null
        );
        setReplyText("");
        setComments((prev) => [...prev, normalized]);
        bumpArtworkCommentCount(1);
        setToast("Comment posted");
        const newId = Number(normalized?.id);
        if (Number.isFinite(newId)) setPendingFocusCommentId(newId);
      } catch (e) {
        setToast(e?.message || "Failed to post comment");
        throw e;
      } finally {
        setSending(false);
      }
    },
    [bumpArtworkCommentCount, id, replyText, sending, user, viewerId]
  );

  const submitInlineReply = useCallback(
    async (payload) => {
      const parent = payload?.comment;
      const parentId = Number(parent?.id ?? parent?.comment_id);
      const text = String(payload?.text ?? inlineReplyText ?? "").trim();
      if (!text || !viewerId || !id || inlineSending) return;
      if (!Number.isFinite(parentId)) return;
      try {
        setInlineSending(true);
        setCommentThreadActionErr("");
        const created = await postArtworkComment({
          artworkId: id,
          text,
          parentCommentId: parentId,
        });
        const normalized = mergeViewerOntoOwnClubCommentIfNeeded(
          normalizeClubCommentRow(created),
          viewerId,
          user ? { username: user.username, profile_picture: user.profile_picture } : null
        );
        setInlineReplyText("");
        setActiveReplyTargetId(null);
        setComments((prev) => [...prev, normalized]);
        bumpArtworkCommentCount(1);
        setToast("Reply posted");
        const newId = Number(normalized?.id);
        if (Number.isFinite(newId)) setPendingFocusCommentId(newId);
      } catch (e) {
        setToast(e?.message || "Failed to post reply");
        throw e;
      } finally {
        setInlineSending(false);
      }
    },
    [bumpArtworkCommentCount, id, inlineReplyText, inlineSending, user, viewerId]
  );

  const onCommentMutated = useCallback((raw) => {
    if (!raw || typeof raw !== "object") return;
    const nid = Number(raw.comment_id ?? raw.id);
    if (!Number.isFinite(nid)) return;
    setComments((prev) =>
      prev.map((c) => {
        const cid = Number(c.comment_id ?? c.id);
        if (!Number.isFinite(cid) || cid !== nid) return c;
        return mergeClubCommentFromServer(c, raw);
      })
    );
  }, []);

  const onToggleCommentLike = useCallback(
    async (comment) => {
      const cid = Number(comment?.id ?? comment?.comment_id);
      if (!Number.isFinite(cid)) return;
      if (!user) {
        nav("/login");
        return;
      }
      if (commentLikeBusyById[cid]) return;

      const row = comments.find((c) => Number(c.comment_id ?? c.id) === cid);
      const optimisticState = buildOptimisticLikeState({
        liked_by_viewer: row?.liked_by_viewer,
        like_count: row?.like_count,
      });

      setCommentLikeBusyById((prev) => ({ ...prev, [cid]: true }));
      setComments((prev) =>
        prev.map((c) =>
          Number(c.comment_id ?? c.id) === cid
            ? {
                ...c,
                liked_by_viewer: optimisticState.optimisticLiked,
                like_count: optimisticState.optimisticCount,
              }
            : c
        )
      );

      try {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const res = await fetch(`${API_BASE}/comments/${cid}/like`, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to toggle comment like.");
        const json = await res.json();
        const canonical = reconcileLikeStateFromResponse(json, optimisticState);
        setComments((prev) =>
          prev.map((c) =>
            Number(c.comment_id ?? c.id) === cid
              ? {
                  ...c,
                  liked_by_viewer: canonical.liked_by_viewer,
                  like_count: canonical.like_count,
                }
              : c
          )
        );
      } catch (err) {
        console.error("Error toggling comment like:", err);
        setComments((prev) =>
          prev.map((c) =>
            Number(c.comment_id ?? c.id) === cid
              ? {
                  ...c,
                  liked_by_viewer: optimisticState.prevLiked,
                  like_count: optimisticState.prevCount,
                }
              : c
          )
        );
        setToast("Failed to update comment like");
      } finally {
        setCommentLikeBusyById((prev) => ({ ...prev, [cid]: false }));
      }
    },
    [commentLikeBusyById, comments, nav, user]
  );

  // Theme constants
  const shellBg = "bg-[#070a10]";
  const line = "border-white/10";

  const infoWrap =
    "mt-8 rounded-2xl border border-white/10 bg-white/[0.03] shadow-[0_18px_80px_rgba(0,0,0,0.55)]";
  const infoTitle = "text-2xl sm:text-3xl font-extrabold tracking-tight text-white";
  const infoBy = "text-sm font-semibold text-white/70";
  const infoMeta = "text-sm text-white/70";
  const infoCounts = "mt-4 flex flex-wrap items-center gap-5 text-sm text-white/80";
  const countPill =
    "inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1.5 font-semibold text-white/85";
  const tagPill =
    "inline-flex items-center rounded-md border border-white/14 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/[0.07] transition";
  const actionBtn =
    "inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white transition";
  const iconBtn =
    "inline-flex items-center justify-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] p-2.5 text-white/90 hover:bg-white/[0.07] hover:border-white/20 transition";
  const railCard =
    "rounded-xl border border-white/10 bg-white/[0.03] shadow-[0_18px_70px_rgba(0,0,0,0.45)]";

  if (loading) {
    return (
      <div className={`min-h-screen ${shellBg} text-white`}>
        <div className="mx-auto max-w-[1400px] px-4 py-6">
          <div className="h-6 w-32 rounded bg-white/10" />
          <div className="mt-6 h-[78vh] w-full rounded bg-white/10" />
        </div>
      </div>
    );
  }

  if (err || !artwork) {
    return (
      <div className={`min-h-screen ${shellBg} text-white`}>
        <div className="mx-auto max-w-[900px] px-4 py-10">
          <button
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className={`mt-6 ${railCard} p-6`}>
            <div className="text-lg font-semibold">Unable to open artwork</div>
            <div className="mt-2 text-sm text-white/70">{err || "Something went wrong."}</div>
            <div className="mt-5">
              <Link
                to="/arthouse"
                className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/[0.10] transition"
              >
                Go to ArtHouse
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use latest favorites count from data if present (synced by DB calls)
  const favoritesCount = Number(data?.artwork?.favorites ?? stats.favorites ?? 0);
  const artworkLiked = Boolean(
    data?.artwork?.liked_by_viewer ?? data?.artwork?.liked ?? artwork?.liked_by_viewer ?? artwork?.liked
  );
  const artworkLikeCount = Number(
    data?.artwork?.like_count ?? data?.artwork?.likes ?? artwork?.like_count ?? artwork?.likes ?? 0
  );

  return (
    <div className={`min-h-screen ${shellBg} text-white`}>
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[#070a10]" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_520px_at_50%_0%,rgba(255,255,255,0.09),transparent_60%)] opacity-30" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.08),rgba(0,0,0,0.78))]" />
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <div className="rounded-full border border-white/12 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white/95 backdrop-blur-xl">
            {toast}
          </div>
        </div>
      ) : null}

      <div className="mx-auto max-w-[1400px] px-4 py-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => nav(-1)}
            className="
              inline-flex items-center gap-2 text-sm font-semibold
              text-white/85 hover:text-white transition
              -ml-2 sm:-ml-1
            "
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
          <div className="min-w-0">
            <div className="flex items-center justify-center">
              {img ? (
                <img
                  src={img}
                  alt={title}
                  className="
                    max-h-[78vh] w-auto max-w-full object-contain
                    shadow-[0_25px_120px_rgba(0,0,0,0.70)]
                  "
                  loading="lazy"
                />
              ) : (
                <div className="grid h-[70vh] w-full place-items-center text-white/70">
                  No image available
                </div>
              )}
            </div>

            <div className={`mt-5 flex flex-wrap items-center justify-between gap-3 border-t ${line} pt-4`}>
              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={onFavorite}
                  className={actionBtn}
                  disabled={loadingFavorite}
                  title={isFavorited ? "Remove from favourites" : "Add to favourites"}
                >
                  <Bookmark className="h-4 w-4" />
                  {isFavorited ? "Favourited" : "Add to Favourites"}
                  <span className="ml-1 text-white/55">{formatCount(favoritesCount)}</span>
                </button>

                <button
                  onClick={() =>
                    document.getElementById("comments")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                  className={actionBtn}
                >
                  <MessageCircle className="h-4 w-4" />
                  Comment
                  <span className="ml-1 text-white/55">{formatCount(commentsCount)}</span>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={onLike} className={iconBtn} aria-label="Like">
                  <Heart
                    className={`h-4 w-4 ${
                      artworkLiked ? "fill-current text-emerald-300" : "text-white/90"
                    }`}
                  />
                  <span className="text-xs tabular-nums text-white/85">{formatCount(artworkLikeCount)}</span>
                </button>
                <button onClick={onShare} className={iconBtn} aria-label="Share">
                  <Share2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <section className={infoWrap}>
              <div className="p-6 sm:p-7">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className={infoTitle}>{title}</h2>
                      {isAI ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-2 py-1 text-[11px] font-semibold text-emerald-200">
                          <Sparkles className="h-3.5 w-3.5" />
                          AI
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-1 flex items-center gap-2">
                      <span className={infoBy}>by</span>
                      {artistId ? (
                        <Link
                          to={`/arthouse/artist/${artistId}`}
                          className="text-sm font-extrabold text-white/90 hover:text-white transition"
                        >
                          {artistName}
                        </Link>
                      ) : (
                        <span className="text-sm font-extrabold text-white/90">{artistName}</span>
                      )}

                      {artistId ? (
                        <button
                          onClick={() => alert("Hook this to POST /artists/:id/follow later.")}
                          className="ml-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition"
                          type="button"
                        >
                          + Follow
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className={infoMeta}>{stats.createdAt ? `Published: ${safeDateLabel(stats.createdAt)}` : ""}</div>
                </div>

                <div className={infoCounts}>
                  <div className={countPill}>
                    <Bookmark className="h-4 w-4 text-white/70" />
                    <span>{formatCount(favoritesCount)} Favourites</span>
                  </div>
                  <div className={countPill}>
                    <MessageCircle className="h-4 w-4 text-white/70" />
                    <span>{formatCount(commentsCount)} Comments</span>
                  </div>
                  <div className={countPill}>
                    <span className="inline-block h-2 w-2 rounded-full bg-white/60" />
                    <span>{formatCount(stats.views)} Views</span>
                  </div>
                </div>

                <div className={`mt-5 border-t ${line}`} />

                {tags.length ? (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {uniq(tags)
                      .slice(0, 18)
                      .map((t) => (
                        <Link
                          key={t}
                          to={`/arthouse/gallery?tag=${encodeURIComponent(t)}`}
                          className={tagPill}
                        >
                          {t}
                        </Link>
                      ))}
                  </div>
                ) : (
                  <div className="mt-6 text-sm text-white/55">No tags yet.</div>
                )}

                {description ? <div className="mt-6 text-[15px] leading-7 text-white/85">{description}</div> : null}
              </div>
            </section>

            <div id="comments" className="mt-10">
              <div className="text-sm font-semibold text-white/90">
                Comments{" "}
                <span className="text-white/50">{commentsCount ? `(${formatCount(commentsCount)})` : ""}</span>
              </div>

              <div className={`mt-3 ${railCard} p-4`}>
                <ThreadComposer
                  viewerId={viewerId}
                  canParticipate={Boolean(user)}
                  replyText={replyText}
                  sending={sending}
                  onReplyTextChange={setReplyText}
                  onSubmit={submitReply}
                  allowAttachments={false}
                  isSearchMode={searchMode}
                  searchText={commentSearchText}
                  onSearchTextChange={setCommentSearchText}
                  onExitSearchMode={() => {
                    setSearchMode(false);
                    setCommentSearchText("");
                  }}
                  searchFocusReturnRef={searchCommentsTriggerRef}
                  className="px-0 py-0 border-0"
                />
                {user ? (
                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
                    <div className="relative" ref={sortMenuRef}>
                      <button
                        type="button"
                        onClick={() => setSortMenuOpen((v) => !v)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-white/85 hover:bg-white/[0.07] transition"
                      >
                        Sort by
                        <span className="capitalize text-white/95">{commentSort}</span>
                        <ChevronDown className="h-3.5 w-3.5 text-white/55" aria-hidden />
                      </button>
                      {sortMenuOpen ? (
                        <div className="absolute left-0 top-full z-20 mt-1 min-w-[9rem] rounded-xl border border-white/12 bg-[#0c1018] py-1 shadow-lg">
                          {[
                            { id: "new", label: "New" },
                            { id: "old", label: "Old" },
                            { id: "top", label: "Top" },
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                setCommentSort(opt.id);
                                setSortMenuOpen(false);
                              }}
                              className={`block w-full px-3 py-2 text-left text-xs font-medium transition hover:bg-white/[0.06] ${
                                commentSort === opt.id ? "text-emerald-200/95" : "text-white/80"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <button
                      ref={searchCommentsTriggerRef}
                      type="button"
                      onClick={() => {
                        setSearchMode(true);
                        setSortMenuOpen(false);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-white/85 hover:bg-white/[0.07] transition"
                      aria-expanded={searchMode}
                    >
                      <Search className="h-3.5 w-3.5 shrink-0 text-white/65" aria-hidden />
                      Search comments
                    </button>
                  </div>
                ) : null}
              </div>

              {commentThreadActionErr ? (
                <div className="mt-2 text-sm text-amber-200/90">{commentThreadActionErr}</div>
              ) : null}

              <div className="mt-6">
                {loadingComments ? (
                  <div className="text-sm text-white/50">Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className="text-sm text-white/50">No comments yet. Be the first to comment!</div>
                ) : (
                  <div className={`${railCard} p-4`}>
                    {threadContinuationAnchorId != null ? (
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-3">
                        <span className="text-xs font-medium text-white/65">Thread view</span>
                        <button
                          type="button"
                          onClick={() => setThreadContinuationAnchorId(null)}
                          className="text-xs font-semibold text-emerald-200/90 hover:text-emerald-100 transition"
                        >
                          See full discussion
                        </button>
                      </div>
                    ) : null}
                    <CommentThreadList
                      comments={commentsForThreadList}
                      viewerId={viewerId}
                      canParticipate={Boolean(user)}
                      activeReplyTargetId={activeReplyTargetId}
                      setActiveReplyTargetId={setActiveReplyTargetId}
                      inlineReplyText={inlineReplyText}
                      setInlineReplyText={setInlineReplyText}
                      inlineSending={inlineSending}
                      onSubmitInlineReply={submitInlineReply}
                      onContinueThread={onArtworkContinueThread}
                      highlightedCommentId={commentDeepLink}
                      focusCommentId={pendingFocusCommentId ?? commentDeepLink}
                      slug={null}
                      postId={null}
                      commentSurface="artwork"
                      artworkId={id}
                      onToggleCommentLike={onToggleCommentLike}
                      onCommentMutated={onCommentMutated}
                      onCommentActionError={(m) => setCommentThreadActionErr(m || "")}
                      expandFirstLevelChildren
                      rootCommentSort={commentSort}
                      searchActive={searchMode && !!String(commentSearchText || "").trim()}
                      searchQuery={commentSearchText}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <MoreByArtistRail
              artistId={artistId}
              artistName={artistName}
              currentArtworkId={id}
              maxItems={9}
              railCardClassName={railCard}
            />

            <ShopLinks merchLinks={merchLinks} max={5} />
          </aside>
        </div>
      </div>
    </div>
  );
}